import request from '@teetsh/app/src/utils/request';
import { deserializeProgrammation } from '@teetsh/app/src/containers/Programmations/atoms/serializer';
import { atomsWithQuery } from 'jotai-tanstack-query';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { schoolyearSchoolAtom } from '@teetsh/app/src/containers/App/context/atom';
import { selectAtom, unwrap } from 'jotai/utils';
import { atomWithDefault } from '@teetsh/app/src/utils/atomWithDefaultSimple';
import {
  FontSize,
  Programmation,
  ProgrammationView,
} from '@teetsh/app/src/types/programmations';
import {
  NiveauValue,
  ScholarSystems,
} from '@teetsh/app/src/containers/scholarSystem';

export const PROGRAMMATION_KEY = 'programmation';

export const programmationIdAtom = atom<string | undefined>(undefined);
export const programmationIsV2Atom = atom<boolean>(false);
type ProgrammationMode = 'edit' | 'print' | 'preview';
export const programmationModeAtom = atom<ProgrammationMode>('edit');

export const useGetProgrammationMode = () =>
  useAtomValue(programmationModeAtom);

const setToEditModeAtom = atom(null, (_, set) =>
  set(programmationModeAtom, 'edit'),
);
export const useSetToEditMode = () => useSetAtom(setToEditModeAtom);

export const setToPreviewModeAtom = atom(null, (_, set) =>
  set(programmationModeAtom, 'preview'),
);
export const useSetToPreviewMode = () => useSetAtom(setToPreviewModeAtom);

export const setToPrintModeAtom = atom(null, (_, set) =>
  set(programmationModeAtom, 'print'),
);
export const useSetToPrintMode = () => useAtom(setToPrintModeAtom);

export const progIdAtom = selectAtom(programmationIdAtom, id => {
  if (!id) {
    throw new Error('no programmation id');
  }
  return id;
});
export const useProgrammationId = () => useAtomValue(progIdAtom);

export function fetchProgrammation({
  schoolyearId,
  schoolId,
  programmationId,
  isV2,
}: {
  schoolyearId: string;
  schoolId: string;
  programmationId: string;
  isV2: boolean;
}) {
  let requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/programmations/${programmationId}`;
  if (isV2) {
    requestURL += '?cleanWidth=true';
  }
  return request(requestURL, {
    method: 'GET',
  }).then(prog => deserializeProgrammation(prog));
}

const [promiseProgAtom] = atomsWithQuery(get => ({
  queryKey: [
    get(schoolyearSchoolAtom)?.schoolyearId,
    get(schoolyearSchoolAtom)?.schoolId,
    PROGRAMMATION_KEY,
    get(programmationIdAtom),
  ],
  refetchOnWindowFocus: false,
  queryFn: async ({ queryKey }) => {
    const [schoolyearId, schoolId, , programmationId] = queryKey as any;
    const isV2 = get(programmationIsV2Atom);
    const res = await fetchProgrammation({
      schoolyearId: schoolyearId,
      schoolId: schoolId,
      programmationId: programmationId || '',
      isV2,
    });
    return res;
  },
}));

export const progAtom = atomWithDefault(async get => {
  const res = await get(promiseProgAtom);
  return res;
});

export const programmationNameAtom = selectAtom<
  Programmation | undefined,
  string | undefined
>(unwrap(progAtom), (prog, prev) => (prog ? prog.name : prev));
export const useProgrammationName = () => useAtomValue(programmationNameAtom);

export const progWrapperAtom = selectAtom<
  Programmation | undefined,
  Programmation | undefined
>(unwrap(progAtom), (prog, prev) => (prog ? prog : prev));

export function useProg() {
  return useAtomValue(progWrapperAtom);
}

export const progViewAtom = selectAtom<
  Programmation | undefined,
  ProgrammationView | undefined
>(unwrap(progAtom), (p, prev) => (p ? p.view : prev));
export const useProgView = () => useAtomValue(progViewAtom);

const progNiveauAtom = selectAtom<
  Programmation | undefined,
  NiveauValue | undefined
>(unwrap(progAtom), (p, prev) =>
  p ? p.niveau : prev || ScholarSystems.FR.defaultNiveau,
);
export const useProgNiveau = () => useAtomValue(progNiveauAtom);

const progCycleAtom = selectAtom<Programmation | undefined, string | undefined>(
  unwrap(progAtom),
  (p, prev) => (p ? p.cycle : prev),
);
export const useProgCycle = () => useAtomValue(progCycleAtom);

export const progOnePageMatiereAtom = selectAtom<
  Programmation | undefined,
  boolean | undefined
>(unwrap(progAtom), (p, prev) => (p ? p.onePageMatiere : prev));
export const useOnePageMatiere = () => useAtomValue(progOnePageMatiereAtom);

export const programmationInvertedRowColAtom = selectAtom<
  Programmation | undefined,
  boolean
>(unwrap(progAtom), (p, prev) => (p ? p.invertedRowCol : prev || false));
export const useInverseRowCol = () =>
  useAtomValue(programmationInvertedRowColAtom);

const progHideTitle = selectAtom<Programmation | undefined, boolean>(
  unwrap(progAtom),
  (p, prev) => (p ? p.hideTitle : prev || false),
);
export const useHideTitle = () => useAtomValue(progHideTitle);

const progFontSize = selectAtom<Programmation | undefined, FontSize>(
  unwrap(progAtom),
  (p, prev) => (p ? p.fontSize : prev || 'base'),
);
export const useFontSize = () => useAtomValue(progFontSize);
